import { format } from 'date-fns-tz';
import { parseISO } from 'date-fns';

import { useAuthStore } from '@/stores/auth';

export const formatDate = function (value, dateFormat) {
    if (!value) return;
    value = value instanceof Date ? value.toISOString() : value;

    const authStore = useAuthStore();
    let dateFormatTemplate = dateFormat ? dateFormat : authStore.outputDateFormat;
    return format(parseISO(value), dateFormatTemplate);
};

// Drop the day-of-month token, then the punctuation it leaves orphaned
const monthYearTemplate = function (dateFormatTemplate) {
    return dateFormatTemplate
        .replace(/d+o?/g, '')
        .replace(/([/.-])\s*\1/g, '$1')
        .replace(/[,\s]+/g, ' ')
        .replace(/^[/.,\s-]+|[/.,\s-]+$/g, '');
};

// Formats a value whose precision may be a year, a month/year, or a full date, without inventing the
// missing parts the way formatDate does ('2015' would become 01/01/2015). Unparseable input is
// returned as-is.
export const formatPartialDate = function (value, dateFormat) {
    if (!value) return '';

    const dateString = value instanceof Date ? value.toISOString() : String(value);

    if (/^\d{4}$/.test(dateString)) return dateString;

    try {
        const authStore = useAuthStore();
        const dateFormatTemplate = dateFormat ? dateFormat : authStore.outputDateFormat;

        if (/^\d{4}-\d{2}$/.test(dateString)) {
            return format(parseISO(dateString + '-01'), monthYearTemplate(dateFormatTemplate));
        }

        return formatDate(dateString, dateFormatTemplate);
    } catch (e) {
        return dateString;
    }
};

export const formatTime = function (value, passedConfig = {}) {
    if (!value) return;
    const authStore = useAuthStore();
    let config = { ...{ showTZ: false, outputFormat: authStore.militaryTime ? 'kk:mm' : 'h:mm a' }, ...passedConfig };
    return formatDatetime(value, config);
};

/**
 * A bare wall-clock 'HH:mm' — no date to reason about and no zone to convert — read in the
 * account's clock format. Deliberately not built on formatTime, which parses an instant, and
 * deliberately not the store's kk, which renders midnight as 24:00.
 *
 * @param {string} value - 'HH:mm' or 'HH:mm:ss'
 * @returns {string}
 */
export const formatClockTime = function (value) {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? '').trim());
    if (!match) return value ? String(value) : '';

    const hours = Number(match[1]);
    const minutes = match[2];
    if (!Number.isFinite(hours) || hours > 23 || Number(minutes) > 59) return String(value);

    const authStore = useAuthStore();
    if (authStore.militaryTime) return `${String(hours).padStart(2, '0')}:${minutes}`;

    return `${hours % 12 === 0 ? 12 : hours % 12}:${minutes} ${hours >= 12 ? 'PM' : 'AM'}`;
};

export const formatDatetime = function (value, passedConfig = {}) {
    if (!value) return;
    const authStore = useAuthStore();
    let config = {
        showTZ: true,
        outputFormat: authStore.outputDateFormat + (authStore.militaryTime ? ' kk:mm' : ' h:mm a'),
        ...passedConfig,
    };

    // Remove the timezone from the output format if `removeTZ` is true
    if (config.showTZ && !config.removeTZ) {
        config.outputFormat += config.outputFormat.slice(-1) === 'z' ? '' : ' z';
    }

    value = value instanceof Date ? value.toISOString() : value;

    if (config.timezone) {
        return format(parseISO(value), config.outputFormat, { timeZone: config.timezone });
    }

    return format(parseISO(value), config.outputFormat);
};
